import * as React from 'react';
import { observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';


import { IOdd, MtsTicketStore, IGameGroupStore, gameStore, OfferType, LiveStreamErrorType, ProgressStateModel, LiveStream } from 'eTip--common';
import { LiveEventStore } from '../../models/live/liveEvent';
import { EventStatsTipos } from './../../api/eventStats';

import { BetradarVideoPlayer, LiveStreamLoader, NoResultsCustom } from 'common--design-library';
import { IDetailBase, LiveEventDetailHeader, MatchTracker } from 'desktop--design-library';
import { FluidContainer, Row, Col } from 'common--design-library';
import * as classNames from 'classnames';

import { renderLiveEventGame } from './eventDetailGames';

import { translation } from 'easit--localization';

export interface IEventDetailProps {
    canShowInTabs: boolean;
    detail: Instance<typeof LiveEventStore>;
    ticketWrapper: Instance<typeof MtsTicketStore>;
    isFavoriteDetail?: boolean;
    isMatchTracker: boolean;
    matchTrackerClick: () => void;
    isMatchTrackerAllowed?: boolean;
    isLiveStreamAllowed: boolean;
    onGoToEventDetail: () => void;
    containerHeight: number;
    setContainerHeight: (isActionsExpanded: boolean) => void;
}

export const EventDetail: React.FC<IEventDetailProps> = observer((props: IEventDetailProps) => {
    const getGameGroup = (gg: IGameGroupStore) => {
        return <div className="games">
            {gg.games.map((g: Instance<typeof gameStore>) => {
                return renderLiveEventGame(g.id, {
                    id: g.id,
                    name: g.name,
                    hasDetails: g.hasDetails,
                    isActive: (oddKey: string) => { return !!props.ticketWrapper.activeTips[oddKey] },
                    isInActive: (oddKey: string) => { return !!props.ticketWrapper.inactiveTips[oddKey] },
                    onClick: (detail: IDetailBase, odd: IOdd) => {
                        props.ticketWrapper.oddsSetTipOnCouponAsync(odd,
                            {
                                eventName: props.detail.apiData.name,
                                eventDate: props.detail.apiData.startDate,
                                gameName: g.name,
                                hasDetail: g.hasDetails,
                                detailName: detail.name,
                                number: detail.number,
                                offerType: OfferType.Live
                            });
                    },
                    details: g.details,
                    isOpen: props.detail.isGameOpen(g.id),
                    toggleOpen: () => { props.detail.setGameIsOpen(g.id, !props.detail.isGameOpen(g.id)) },
                    gameInfo: g.info
                });
            })}
        </div>
    }

    const getRatesContent = (store: Instance<typeof LiveEventStore>) => {
        return store.apiData.gameGroups &&
            store.gameGroupByFavorites.map((gg) => {
                return <div className="game-group" key={gg.id}>
                    <div className="game-group-header" onClick={(e) => { e.stopPropagation(); store.setGameGroupIsOpen(gg.id, !store.isGameGroupOpen(gg.id)); }}>
                        <div style={{ cursor: "pointer", float: "left" }}>
                            <span className={`${store.isGameGroupOpen(gg.id) ? "fa fa-caret-down" : "fa fa-caret-up"}`}></span>
                        </div>
                        <div style={{ cursor: "pointer", float: "right" }}>
                            <span className={`${store.isGroupFavorite(gg.id) ? "fa fa-star" : "far fa-star"}`} onClick={(e) => { e.stopPropagation(); store.setGameGroupIsFavorite(gg.id, !store.isGroupFavorite(gg.id)); }}></span>
                        </div>
                        <span className="ml-1">{gg.name}</span>
                    </div>

                    {store.isGameGroupOpen(gg.id) && getGameGroup(gg)}
                </div>
            });
    }

    const getRatesContentInTabs = (store: Instance<typeof LiveEventStore>) => {
        return store.apiData.gameGroups &&
            <>
                <FluidContainer>
                    <Row className="game-group-tabs">
                        {store.apiData.gameGroups.map((gg, i) => ( //Create by tabs
                            <Col key={i} className={classNames('text-truncate', { selected: gg.id === store.selectedGameGroupTab })}>
                                <div className="text-truncate w-100"
                                    onClick={() => store.setGameGroupTab(gg.id)}
                                >
                                    {gg.name || 'Group: ' + gg.id}
                                </div>
                            </Col>
                        ))}
                    </Row>
                </FluidContainer>

                {/* Render games in a group */}
                {store.apiData.gameGroups.find(x => x.id === store.selectedGameGroupTab)?.games.map((g: Instance<typeof gameStore>) => {
                    return renderLiveEventGame(g.id, {
                        id: g.id,
                        name: g.name,
                        hasDetails: g.hasDetails,
                        isActive: (oddKey: string) => { return !!props.ticketWrapper.activeTips[oddKey] },
                        isInActive: (oddKey: string) => { return !!props.ticketWrapper.inactiveTips[oddKey] },
                        onClick: (detail: IDetailBase, odd: IOdd) => {
                            props.ticketWrapper.oddsSetTipOnCouponAsync(odd,
                                {
                                    eventName: props.detail.apiData.name,
                                    eventDate: props.detail.apiData.startDate,
                                    gameName: g.name,
                                    hasDetail: g.hasDetails,
                                    detailName: detail.name,
                                    number: detail.number,
                                    offerType: OfferType.Live
                                });
                        },
                        details: g.details,
                        isOpen: props.detail.isGameOpen(g.id),
                        toggleOpen: () => { props.detail.setGameIsOpen(g.id, !props.detail.isGameOpen(g.id)) },
                        gameInfo: g.info
                    });
                })}
            </>
    }

    const processVideoShowError = (liveStream: Instance<typeof LiveStream>, errorType: LiveStreamErrorType): void => {
        liveStream.setErrorWhenShown(errorType);
    }

    const processVideoUrlsAreExpired = (liveStream: Instance<typeof LiveStream>): void => {
        liveStream.prepareUrlsAsync();
    }


    const getLiveStreamContent = () => {
        const liveStream: Instance<typeof LiveStream> = props.detail.liveStream;
        if (liveStream.state == ProgressStateModel.NotInitialized) {
            return null;
        }
        return <div className="p-3">
            {liveStream.state == ProgressStateModel.Loading && <LiveStreamLoader />}
            {liveStream.state == ProgressStateModel.Failed && <NoResultsCustom message={liveStream.errorMessage}>
                {liveStream.errorCanShowTryAgain && <button className="btn btn-primary btn-sm ml-3" onClick={() => { props.detail.liveStream.prepareUrlsAsync(); }}>{translation().liveStream_TryAgain}</button>}
            </NoResultsCustom>
            }
            {liveStream.state === ProgressStateModel.Success &&
                <BetradarVideoPlayer
                    eventId={liveStream.eventId}
                    eventDiscipline={liveStream.eventDiscipline}
                    eventCategoryPath={liveStream.eventCategoryPath}
                    canResizeAutomatically={true}
                    linkHls={liveStream.videoUrlHls}
                    linkDash={liveStream.videoUrlDash}
                    isTouchUI={true}
                    refreshSources={() => processVideoUrlsAreExpired(props.detail.liveStream)}
                    errorLoadingContent={(errorType: LiveStreamErrorType) => processVideoShowError(props.detail.liveStream, errorType)}
                    elementId={"video_" + liveStream.externalId}
                    //jwPlayerKey={liveStream.jwPlayerKey}
                    //jwPlayerBasePath={liveStream.jwPlayerBasePath}
                    loggingService={liveStream.loggingService}
                    shownForMonitoring={() => { if (props.detail.liveStream) { props.detail.liveStream.videoShownForMonitoring(); } }}
                    canLimitWidth={props.isFavoriteDetail}
                    popupWindowData={{
                        streamId: liveStream.externalId,
                        userId: props.detail.userIdForLiveStreamPopup,
                        eventId: props.detail.id,
                        eventName: props.detail.apiData.name,
                        token: liveStream.tokenForPopup,
                        eventDiscipline: liveStream.eventDiscipline,
                        eventCategoryPath: liveStream.eventCategoryPath
                    }}
                    closeVideo={() => { props.detail.liveStream.hide() }}
                />
            }
        </div>
    }

    const getMatchTrackerContent = () => {
        const matchTracker: Instance<typeof LiveStream> = props.detail.matchTracker;
        if (matchTracker.state == ProgressStateModel.NotInitialized) {
            return null;
        }
        return (
            <div className="p-3"><MatchTracker
                state={matchTracker.state}
                externalId={matchTracker.externalId}
                operator={matchTracker.operator}
                elementIdBase="matchtracker-evt-detail"
                eventName={props.detail.apiData.name}
                errorLoadingContent={matchTracker.setErrorWhenShown}
                languageId={matchTracker.languageId}
                prepareUrlsAsync={matchTracker.prepareUrlsAsync}
                shownForMonitoring={matchTracker.videoShownForMonitoring}
                stoppedForMonitoring={matchTracker.videoStoppedForMonitoring}
                matchTrackerKey={props.detail.matchtrackerKey}
            /></div>
        )
    }

    return (
        <div className="live-event-detail" data-test-role="event-detail" style={{ minHeight: props.containerHeight }}>
            <LiveEventDetailHeader
                onToggleActions={props.setContainerHeight}
                eventId={props.detail.apiData.id}
                actions={props.detail.apiData.eventActions}
                categoryBreadcrumbs={props.detail.apiData.categoryPath}
                competitors={props.detail.apiData.competitors}
                graphicsPresentationId={props.detail.apiData.graphicsPresentationId}
                eventTime={props.detail.apiData.eventTime}
                importantResults={props.detail.apiData.importantResults}
                isFavorite={props.isFavoriteDetail}
                hasResults={props.detail.apiData.hasResults}
                mainResult={props.detail.apiData.mainResult}
                otherResults={props.detail.apiData.otherResults}
                stateName={props.detail.apiData.stateName}
                onLiveStreamClick={props.isLiveStreamAllowed && props.detail.liveStream
                    ? () => {
                        if (props.detail.matchTracker) {
                            props.detail.matchTracker.hide();
                        }
                        if (props.detail.liveStream.state === ProgressStateModel.NotInitialized) {
                            props.detail.liveStream.prepareUrlsAsync();
                        } else {
                            props.detail.liveStream.hide();
                        }
                    } : null}
                canShowLiveStreamIcon={!!props.detail.liveStream}
                onMatchTrackerClick={props.isMatchTrackerAllowed && props.detail.matchTracker
                    ? () => {
                        if (props.detail.liveStream) {
                            props.detail.liveStream.hide();
                        }
                        if (props.matchTrackerClick) {
                            props.matchTrackerClick();
                        }
                        else {
                            if (props.detail.matchTracker.state === ProgressStateModel.NotInitialized) {
                                props.detail.matchTracker.prepareUrlsAsync();
                            } else {
                                props.detail.matchTracker.hide();
                            }
                        }
                    }
                    : null
                }
                canShowMatchTrackerIcon={!!props.detail.matchTracker}
                onGoToEventDetail={props.onGoToEventDetail}
                statistics={props.detail.apiData.statistics}
                onStatisticsOpen={() => {
                    const stats = new EventStatsTipos();
                    stats.viewStatistics(props.detail.apiData.statistics, props.detail.apiData.name)
                }
                }
            />
            {props.detail.matchTracker && getMatchTrackerContent()}
            {props.detail.liveStream && getLiveStreamContent()}
            <div className="live-event-detail-content">
                {true ? getRatesContentInTabs(props.detail) : getRatesContent(props.detail)}
            </div>
        </div>
    );
});
